import React, { useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search } from "lucide-react";
import NoResultsMessage from "./NoResultsMessage";
import VirtualizedGridView from "./VirtualizedGridView";
import LoadingPlaceholders from './LoadingPlaceholders';
import { useWindowSize, getColumnCount } from './gridUtils';
import { useSettings } from '../../lib/hooks/useSettings';

const GridView = ({ 
  themeMode, 
  groupedArray, 
  isLoading, 
  applications,
  showAiSummaries, 
  selectedImage, 
  setSelectedImage,
  searchQuery,
  setSearchQuery,
  selectedFilter,
  setSelectedFilter,
  expandedIndex,
  setExpandedIndex,
  cardHoverVariants,
  favorites,
  toggleFavorite,
  isCategoryLoading,
  loaderRef,
  hasMoreGroups = false,
  isResolvingSearch = false,
  resolvingSearchTitle,
  resolvingSearchDescription
}) => {
  const containerRef = useRef(null);
  
  // On mount
  useEffect(() => {
  }, []);

  const width = useWindowSize();
  const columnCount = getColumnCount(width);

  // Determine if we need to show placeholder content
  const showResolvingSearch = Boolean(isResolvingSearch && groupedArray.length === 0);
  const showPlaceholderContent = isLoading || isCategoryLoading || showResolvingSearch;

  // Animation variants for loading state
  const contentVariants = {
    loading: {
      opacity: 1,
      filter: "none",
      transition: { duration: 0.3 }
    },
    loaded: {
      opacity: 1,
      filter: "blur(0px)",
      transition: { duration: 0.3 }
    }
  };

  // Animation variants for loading overlay
  const loadingOverlayVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0.9,
      transition: { duration: 0.2 }
    },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.3 } 
    }
  };

  // Placeholder variants for loading state
  const placeholderVariants = {
    initial: { 
      opacity: 0, 
      y: 10,
    },
    animate: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.3 } 
    },
    exit: { 
      opacity: 0, 
      y: -10,
      transition: { duration: 0.2 } 
    }
  };
  
  const settings = useSettings();
  const loadingOverlayClassName = showResolvingSearch
    ? "absolute inset-x-0 top-0 z-20 flex min-h-[360px] items-start justify-center px-4 pt-8 pointer-events-none sm:pt-10"
    : "absolute inset-0 z-20 flex flex-col items-center justify-center px-4 pointer-events-none";
  const placeholderLayerClassName = showResolvingSearch
    ? "opacity-45 blur-[1px]"
    : "opacity-70";
  
  if (groupedArray.length === 0 && !showPlaceholderContent) {
    return (
      <NoResultsMessage
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        selectedFilter={selectedFilter}
        setSelectedFilter={setSelectedFilter}
        themeMode={themeMode}
      />
    );
  }
  
  return (
    <div className="w-full" ref={containerRef}>
      {/* Content Area */}
      <motion.div
        variants={contentVariants}
        animate={showPlaceholderContent ? "loading" : "loaded"}
        className="relative min-h-[300px]"
      >
        {/* Loading Overlay */}
        <AnimatePresence>
          {showPlaceholderContent && (
            <motion.div
              className={loadingOverlayClassName}
              variants={loadingOverlayVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              role="status"
              aria-live="polite"
            >
              <div
                className={`text-center ${showResolvingSearch ? 'w-full max-w-xl border border-[var(--c-border)] bg-[var(--c-surface)]/95 px-5 py-5 shadow-[0_18px_50px_rgba(0,0,0,0.22)] backdrop-blur sm:px-7 sm:py-6' : ''}`}
                data-planning-search-resolving={showResolvingSearch ? 'true' : undefined}
              >
                <div className="w-16 h-16 mx-auto mb-4">
                  <div className="w-full h-full rounded-full border-t-2 border-b-2 border-[var(--c-accent)] animate-spin-slow"></div>
                </div>
                <h3 className="heading-3 text-[var(--c-text-primary)] mb-2">
                  {showResolvingSearch ? (resolvingSearchTitle || 'Searching the planning feed') : 'Loading'}
                </h3>
                <p className="body-m text-[var(--c-text-secondary)]">
                  {showResolvingSearch
                    ? (resolvingSearchDescription || 'Matching applications will appear automatically.')
                    : 'Retrieving planning application data...'}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Placeholder or Actual Content */}
        <AnimatePresence mode="wait">
          {showPlaceholderContent ? (
            <motion.div
              key="placeholder-content"
              className={placeholderLayerClassName}
              variants={placeholderVariants}
              initial="initial" 
              animate="animate"
              exit="exit"
            >
              <LoadingPlaceholders columnCount={columnCount} />
            </motion.div>
          ) : (
            <motion.div
              key="actual-content"
              variants={placeholderVariants}
              initial={settings.shouldShowAnimations ? { opacity: 0, y: 20 } : { opacity: 1, y: 0 }}
              animate={{ opacity: 1, y: 0 }}
              transition={settings.shouldShowAnimations ? { 
                duration: 0.4, 
                ease: "easeOut"
              } : { duration: 0 }}
            >
              <VirtualizedGridView
                groupedArray={groupedArray}
                themeMode={themeMode}
                applications={applications}
                showAiSummaries={showAiSummaries}
                selectedImage={selectedImage}
                setSelectedImage={setSelectedImage}
                expandedIndex={expandedIndex}
                setExpandedIndex={setExpandedIndex}
                cardHoverVariants={cardHoverVariants}
                favorites={favorites}
                toggleFavorite={toggleFavorite}
              />
              
              {/* Elegant Loading Indicator */}
              {groupedArray.length > 0 && hasMoreGroups && (
                <div className="flex justify-center mt-8 pb-12">
                  <div className="text-center">
                    <div className="w-10 h-10 mx-auto mb-3">
                      <div className="w-full h-full rounded-full border-t-2 border-[var(--c-accent)] animate-spin-slow"></div>
                    </div>
                    <p className="text-[var(--c-text-secondary)] text-sm">Loading more results...</p>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Loader reference element - positioned outside AnimatePresence for better detection */}
        <div ref={loaderRef} className="h-20 w-full opacity-0 pointer-events-none mb-20" aria-hidden="true" />
        
        {/* Additional loading indicator at bottom for better visibility */}
        {groupedArray.length > 0 && hasMoreGroups && (
          <div className="flex justify-center mt-6 mb-12">
            <div className="flex flex-col items-center">
              <div className="w-8 h-1 bg-[var(--c-accent)] rounded-full mb-2"></div>
              <div className="w-16 h-1 bg-[var(--c-accent-muted)] rounded-full"></div>
            </div>
          </div>
        )}
        
        {/* No Results Message */}
        {groupedArray.length === 0 && !showPlaceholderContent && (
          <div className="card p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 opacity-40">
              <Search size={64} className="text-[var(--c-text-disabled)]" />
            </div>
            <h3 className="heading-3 text-[var(--c-text-primary)] mb-2">No results found</h3>
            <p className="body-m text-[var(--c-text-secondary)] mb-6 max-w-lg mx-auto">
              No planning applications match your current filters.
              Try adjusting your search criteria or clearing filters.
            </p>
            <button
              onClick={() => {
                setSelectedFilter('all');
                setSearchQuery('');
              }}
              className="btn--primary"
            >
              Clear all filters
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default GridView;
